
console.log("animation loaded");

/* ---------- text-ready gate -------------------------------------------------
   Both webfaces are declared `font-display: block` (style-globals.css.liquid), so
   during the block period text is INVISIBLE but STILL LAID OUT — using the
   fallback face's metrics. Every box whose height comes from text (the PDP peek
   bar: product.type + the -jp._title metafield + the family SKU row) therefore
   measures wrong until the real font lands, and reflows when it does. That reflow
   is the intermittent bottom-bar shift: it only shows when the fonts aren't
   already cached, which is why it's occasional and worse on mobile.

   The fix is to treat "the text is final" as part of loading, exactly like an
   image decode: __textReady resolves once both faces are in, and the loading
   placeholders below (plus revealSwappedImages in script-product.js) hold until
   it does. So the loading square stays up across the font swap and the reflow
   happens behind it, never in front of the user.

   document.fonts.ready alone is not enough: both faces carry a unicode-range, and
   a subset is only fetched once matching glyphs are actually laid out — so on a
   page that hasn't painted JP text yet, ready can resolve before the JP face has
   even been requested. fonts.load() with a glyph from each range forces both into
   the pending set first. Capped at 3s so a dead font CDN can never strand the
   placeholders (matches the block period's own timeout). */
window.__textReady = (function () {
  var d = document;
  if (!d.fonts || !d.fonts.ready) return Promise.resolve();
  var forced;
  try {
    forced = Promise.all([
      d.fonts.load('10px "NEUE HAAS DISPLAY LIGHT"', "A"),
      d.fonts.load('10px "ZEN KAKU GOTHIC NEW REGULAR"', "\u3042")
    ]);
  } catch (e) {
    forced = Promise.resolve();
  }
  var settled = forced
    .catch(function () {})
    .then(function () { return d.fonts.ready; })
    .then(function () {});
  return Promise.race([
    settled,
    new Promise(function (r) { setTimeout(r, 3000); })
  ]);
})();

/* Sync once resolved: after the first paint of a session the fonts are in, so
   every later reveal (grid re-entry, a family swap, bfcache restore) runs on the
   synchronous path and no placeholder is held for a frame it doesn't need to be. */
window.__textReadyDone = false;
window.__textReady.then(function () { window.__textReadyDone = true; });

/* Run fn now if the text is already final, else once it is. */
window.__whenTextReady = function (fn) {
  if (window.__textReadyDone) fn();
  else window.__textReady.then(fn);
};

/* Only images whose layout is COUPLED to text wait on the fonts. The PDP images
   are: their height is calc(100svh - var(--pdp-peek)) and the peek is the text
   block, so revealing them before the text settles is what shows the shift. The
   home/collection grid is a fixed aspect-ratio grid with no text in it — gating it
   would delay the first paint of the whole grid for nothing. */
function needsTextGate(image) {
  return !image.classList.contains("product-grid-image");
}

/* Reveal gate: run now for grid images, after the text for PDP images. */
function revealWhenReady(image, fn) {
  if (needsTextGate(image) && typeof window.__whenTextReady === "function") {
    window.__whenTextReady(fn);
  } else {
    fn();
  }
}

function handleImageLoad(event) {
  const img = event.target;
  const container = img.closest(".img-and-loading-container");
  const loadingPlaceholder = container && container.querySelector(".image-loading");

  // The bytes are in, but the surrounding TEXT may still be laid out in fallback
  // metrics (see the text-ready gate above) — revealing now would put the reflow
  // on screen. Hold the placeholder over it until the fonts land as well.
  revealWhenReady(img, function () {
    // Hide the loading placeholder when the image loads
    if (container) container.classList.remove("is-loading"); // release the placeholder slot
    if (loadingPlaceholder) loadingPlaceholder.style.display = "none"; // Hide loading animation
    img.style.display = "block"; // Show the loaded image
    img.style.visibility = ""; // lazy images were held invisible (in flow) — see below
  });
}

function handleImageError(event) {
  const img = event.target;
  const container = img.closest(".img-and-loading-container");
  const loadingPlaceholder = container && container.querySelector(".image-loading");

  // Hide the loading placeholder if the image fails to load
  if (container) container.classList.remove("is-loading");
  if (loadingPlaceholder) loadingPlaceholder.style.display = "none"; // Hide loading animation
  img.style.display = "none"; // Hide the image
}

function initializeImageLoading(root) {
  // Select all images in the product grid and product page (including newly loaded ones).
  // `root` scopes it: the PDP body-swap passes its own container so an open doesn't
  // re-walk (and re-listen) every grid image on the page inside the click.
  const productImages = (root || document).querySelectorAll(
    ".product-grid-image, .product-page-image, .instruction-media-image"
  );

  // Add load and error event listeners to each image
  productImages.forEach((image) => {
    const container = image.closest(".img-and-loading-container");
    if (!container) return;
    const loadingPlaceholder = container.querySelector(".image-loading");

    // Already decoded (cached / back-navigation / bfcache restore): the `load`
    // event will NOT fire again, so we must NOT hide the image and wait on it —
    // that's the old bug where revisiting the grid left the spinner stuck on
    // forever. Just show the image, keep the spinner hidden, and move on.
    if (image.complete && image.naturalWidth > 0) {
      // Cached image, but possibly not-yet-cached FONTS: same reasoning as
      // handleImageLoad — hold the placeholder until the text is final so the
      // font-swap reflow happens behind it. Synchronous once the fonts are in.
      // While held, the image is invisible: the placeholder is visible by default
      // (CSS), so leaving the decoded image painted showed the spinner on top of it.
      if (!window.__textReadyDone && needsTextGate(image)) image.style.visibility = "hidden";
      revealWhenReady(image, function () {
        container.classList.remove("is-loading");
        image.style.display = "block";
        image.style.visibility = "";
        if (loadingPlaceholder) loadingPlaceholder.style.display = "none";
      });
      return;
    }

    // Genuinely still loading: show the spinner and let the load/error handlers
    // reveal it. No `src` reassignment — it doesn't reliably re-fire `load`, and
    // the listeners below already cover it.
    //
    // IMPORTANT: a `loading="lazy"` image that is below the fold has NOT started
    // loading yet, and hiding it with `display:none` removes its layout box —
    // which means the browser never scrolls it into view and never loads it, so
    // the `load` event never fires and the spinner spins forever (the stuck
    // instruction-manual placeholder). For lazy images, keep the image in flow
    // (the placeholder is absolutely positioned over it) so the browser can lazy
    // -load it on scroll; the load handler then hides the spinner. Only eager /
    // above-the-fold images are safe to hide, since they load regardless.
    // A lazy image stays in flow but INVISIBLE (visibility keeps the layout box, so
    // lazy-load still triggers): left visible, it painted in progressively under
    // the spinner — the "spinner over an image that's already there" bug.
    if (image.loading !== "lazy") {
      image.style.display = "none";
    } else {
      image.style.visibility = "hidden";
    }
    // The container normally sizes itself off the image; with the image hidden
    // (or lazy and not yet started) it would collapse and swallow the absolutely
    // positioned placeholder. `is-loading` holds a slot open until load/error.
    container.classList.add("is-loading");
    if (loadingPlaceholder) loadingPlaceholder.style.display = "flex";

    image.addEventListener("load", handleImageLoad); // Load event
    image.addEventListener("error", handleImageError); // Error event
  });
}

document.addEventListener("DOMContentLoaded", function () {
  initializeImageLoading();
});

console.log("reset loaded");
window.addEventListener("pageshow", function (event) {
  if (event.persisted) {
    // Detect if page is loaded from cache (back button)
    // Select the element that had the hover effect
    let hoveredElement = document.querySelector(".product-grid-image");
    if (hoveredElement) {
      // Reset the hover styles by removing the class or resetting inline styles
      hoveredElement.classList.remove("hovered-class");
      hoveredElement.style.opacity = ""; // Reset any inline styles
    }
  }
});

// ── Shared collection-modal helpers ──────────────────────────────────────────
// Used by both script-index-filters.js (filter modal) and script-index-product.js
// (product-click modal). Previously each file carried its own byte-identical copy.

// Build the modal product grid: clone the matched containers, fix up lazy-loaded
// images (cloneNode drops their load listeners), inject into modalInner, append
// the footer, and return the cloned containers for the fade-in animation.
function buildModalGrid(sourceContainers, modalInner, appendModalFooter) {
  var grid = document.createElement("div");
  grid.className = "product-grid";
  grid.style.visibility = "visible";

  sourceContainers.forEach(function (original) {
    var clone = original.cloneNode(true);
    clone.style.display = "";
    clone.style.transition = "none";
    clone.style.opacity = "0";

    // cloneNode copies inline display:none from initializeImageLoading but not
    // the load event listeners, so images stay hidden on deep-link opens.
    var cloneImg = clone.querySelector(".product-grid-image");
    if (cloneImg) {
      var loadingEl = clone.querySelector(".image-loading");
      if (cloneImg.complete && cloneImg.naturalWidth > 0) {
        cloneImg.style.display = "block";
        if (loadingEl) loadingEl.style.display = "none";
      } else {
        cloneImg.removeAttribute("loading"); // prevent lazy-load deferral inside modal
        cloneImg.style.display = "none";
        cloneImg.addEventListener("load", function () {
          cloneImg.style.display = "block";
          if (loadingEl) loadingEl.style.display = "none";
        });
      }
    }

    grid.appendChild(clone);
  });

  modalInner.innerHTML = "";
  modalInner.appendChild(grid);
  if (typeof appendModalFooter === "function") appendModalFooter();

  // Every rebuild is a fresh collection — reset to the top. The modal is now in
  // document flow (not a fixed inner-scroll box), so the DOCUMENT is what scrolls;
  // resetting window scroll (not the element's scrollTop) is what puts the new
  // collection at the top. Callers capture the prior home scroll into
  // window.__indexHomeScrollY before this runs, and restore it on close.
  window.scrollTo(0, 0);

  return Array.from(grid.querySelectorAll(".product-grid-image-container"));
}

// Modal-title fade. opacity/transition live in CSS (#collection-modal-title +
// its .visible class); these only toggle the class so there's a single styling
// source of truth instead of inline opacity/transition writes.
function showModalTitle(title) {
  var el = document.getElementById("collection-modal-title");
  if (!el) return;
  el.textContent = title;
  el.classList.remove("no-transition");
  el.classList.add("visible");
}

function hideModalTitle() {
  var el = document.getElementById("collection-modal-title");
  if (!el) return;
  el.classList.remove("visible");
  setTimeout(function () {
    if (!el.classList.contains("visible")) el.textContent = "";
  }, 300);
}

// Instant (no-fade) variant used by the VIEW ALL restore path. Re-enables the
// transition next frame so a later hideModalTitle() still animates out.
function showModalTitleInstant(title) {
  var el = document.getElementById("collection-modal-title");
  if (!el) return;
  el.textContent = title;
  el.classList.add("no-transition");
  el.classList.add("visible");
  requestAnimationFrame(function () { el.classList.remove("no-transition"); });
}

console.log("ln captured");
// Immediately change the body style to black
document.body.style.backgroundColor = "black";

// Language router: English browsers belong on the /en locale. PATH-PRESERVING:
// replacing to bare "/en" threw away the page being visited — a direct hit on
// /pages/information bounced to the /en HOME, and every logo/footer navigation
// through an unprefixed URL cost a visible second navigation (the "logo needs
// more than one click" / nav-flicker on page templates). Locale-aware links
// (routes.root_url in navigation.liquid, Shopify.routes.root in JS) now avoid
// the hop entirely; this stays as the safety net for direct visits.
if (
  navigator.language.startsWith("en") &&
  !window.location.pathname.startsWith("/en")
) {
  window.location.replace(
    "/en" + window.location.pathname + window.location.search + window.location.hash
  );
}
